import {
  ConfigHeaderAction,
  ICompanyInfo,
  IContainerItem,
  ICurrency,
  IPaymentMethod,
  IPhotoEntityReference,
  IShippingMethod,
  ITopbarContainerItem
} from '..';

export type ConsentServiceName =
  | 'chatbase'
  | 'elfsight'
  | 'iframe'
  | 'youtube'
  | 'coockieBot'
  | 'userCentrics'
  | 'tagmanager'
  | 'criteo'
  | 'pinterest'
  | 'bing'
  | 'facebook'
  | 'matomo'
  | 'adobe'
  | 'googleAnalytics'
  | 'gads'
  | 'gmaps'
  | 'awin'
  | 'trustedShop'
  | 'outbrain'
  | 'brevoChat'
  | (string & {});

export type ModuleService = 'chatbaseID';

export type LinkTarget =
  | ConfigHeaderAction
  | 'chainstores'
  | 'facebook'
  | 'imprint'
  | 'agb'
  | 'home'
  | 'instagram'
  | 'linkedIn'
  | 'pinterest'
  | 'privacy'
  | 'revocation'
  | 'search'
  | 'tikTok'
  | 'x'
  | 'xing'
  | 'youtube';

export enum ConsentTool {
  CookieBanner = 'CookieBanner',
  UserCentrics = 'UserCentrics'
}

export type ConsentGroupName = 'necessary' | 'functionality' | 'marketing';

export interface IConsentService {
  /** The cookie corresponding to this service */
  cookie: string;
  /** Name to display to the user inside the consent-management */
  description: string;
  /** The group to wich this service correlates so it will get displayed in it */
  group: ConsentGroupName;
}

/**
 * Interface representing the initialization structure for a service or application.
 * Contains configuration data including menus, payment methods, chain stores, and company information.
 */
export interface IInit {
  menus: IInitMenus;
  /** Store links of the loyalty app, keyed by store — omitted when no link is configured */
  appLinks?: Record<string, string>;
  paymentMethods: IPaymentMethod[];
  shippingMethods: IShippingMethod[];
  /** List of all locations the company operates */
  chainStores: ICompanyInfo[];
  /** The main company location with all information */
  companyInfo: ICompanyInfo;
  linkTargets: Record<LinkTarget, string>;
  /** Whether a checkout page exists (a CMS page carries the checkout link target) — gates the cart/checkout UI. */
  hasCheckout?: boolean;
  /** Whether an inquiry checkout page exists (a CMS page carries the inquiry checkout link target) — gates the Anfrageliste UI. */
  hasInquiry?: boolean;
  currencies: ICurrency[];
  /** The default currency Id to fetch from currencies as a fallback */
  defaultCurrencyID: number;
  consentInfo: Record<ConsentServiceName, IConsentService>;
  modules: Record<ModuleService, string>;
  logo: IPhotoEntityReference;
  /** Alternative logo, e.g. for use in the footer when background color requires a different logo variant */
  logoAlternative?: IPhotoEntityReference;
  /** Fallback image to display when no image is available. If null/undefined, no fallback is shown. */
  noPic?: IPhotoEntityReference;
  socialMediaLinks: Record<string, string>;
  // The name of the consent tool wich is getting used
  consentTool: ConsentTool;
  // Javascript code wich the backend uses to open the cookie banner
  showCookieBanner: string;
  /** Formatting options given by the settings in the backend */
  priceFormatting?: IInitPriceFormat;
  /** Topbar container items (only populated when topbar template is 'custom') */
  topbar?: ITopbarContainerItem[];
  /** Platform-wide form of address (du/Sie) configured in the basic settings. Falls back to 'Formally' when not provided. */
  appellation?: PlatformAppellation;
  /** Type names selected for the typeahead in the application settings — undefined when nothing is configured */
  typeaheadTypes?: string[];
}

export interface IInitPriceFormat {
  /** Separator for decimal values, e.g. 10,99 € */
  decimalSeparator?: string;
  /** Separator for thousand values, e.g. 1.000,99 € */
  thousandSeparator?: string;
  /** Position of the currency sign, e.g. 10,99 € or € 10,99 */
  positionOfSign?: PositionOfCurrencySign;
  /** Separator between the currency sign and the amount, e.g. 10,99 € or 10,99€ */
  spacingSeparator?: string;
}

/** Union type representing the platform-wide form of addressing customers. */
export type PlatformAppellation = 'formally' | 'personally';

export enum PositionOfCurrencySign {
  Before = 0,
  After = 1
}

// Structure for Menus containing menu, footer, and sidebar arrays.
// Keys carry the `cs4-` namespace prefix matching the MENUS keys requested from the backend
// (the backend echoes the requested key verbatim as the response key).
export interface IInitMenus {
  'cs4-menu': IInitMenu[]; // main defined
  'cs4-footer': IInitMenu[]; // footer  menu
  'cs4-sidebar': IInitMenu[]; // sidebar  menu
  'cs4-mobileFooter': IInitMenu[]; // mobile footer menu for dropdown mobile view
  'cs4-footerCopyright': IInitMenu[]; // footer copy right menu
}

/**
 * Interface representing a menu item.
 * Contains basic information like the name, URL, and a unique identifier.
 */
export interface IInitMenu {
  /** name of the menu */
  name?: string;
  /** url of the menu, relevant e.g. for submenus */
  url: string;
  /** iD of the menu */
  iD: number;
  /** glyph icon identifier for the menu item */
  glyph?: string;
  /** photo associated with the menu item (e.g. category image) */
  photo?: IPhotoEntityReference;
  /** nodes submenu */
  nodes: IInitMenu[];
  /** type of the menu to display */
  menuItemType: MenuType;
  /** Advertising from container items in the menu */
  containerItems: IContainerItem[];
}

/**
 * Enum for the different menu types.
 */
export enum MenuType {
  Dropdown = 0,
  MegaMenu = 1,
  Highlight = 2
}